import { Fragment, type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { CornerDownLeft, Crosshair, Download, FileText, HelpCircle, MessagesSquare, SquarePen, SendHorizontal, Sparkles, X } from 'lucide-react';
import { useSidebar } from '../layout/SidebarContext';
import { sendChatMessage } from '@/lib/chat-ai-client';
import { BotaoMicrofone } from './voz/BotaoMicrofone';
import { useDitado } from './voz/useDitado';
import { supabase } from '../providers/supabase/supabase';
import {
  generateMultiCotasPdf,
  generateProposalPdf,
  generateTabelaComparativaPdf,
  type MultiCotasData,
  type ProposalPayload,
  type TabelaComparativaData,
  type UserInfo,
} from '../consortium-groups/proposal-utils';
import { PorQueConsorcioDialog } from '../consortium-groups/PorQueConsorcioDialog';
import { converterParaPropostaDireta, escolherModeloProposta } from './modeloProposta';
import { AjudaNorthIA } from './AjudaNorthIA';
import { ConversasNorthIA } from './ConversasNorthIA';
import { comLinks } from '../misc/linksNoTexto';

type Message = {
  id: number;
  role: 'assistant' | 'user';
  content: string;
};

/**
 * As formas de PDF que o chat pode pedir.
 *
 * `projeto_estruturado` (ou `cota_unica`, que a tool devolve quando a contagem
 * dá 1) são cotas que SOMAM um alvo. `tabela_comparativa` são opções que se
 * EXCLUEM: o cliente leva uma, e somar três variações da mesma cota de R$ 1M
 * imprimiria "R$ 3.000.000 contratados", um número que não existe.
 *
 * O rótulo do `tipo` NÃO escolhe o PDF do projeto — quem escolhe é
 * `escolherModeloProposta`, olhando as cotas e os lances. Ver modeloProposta.ts.
 */
type PdfAction =
  | { tipo: 'projeto_estruturado' | 'cota_unica'; projeto: MultiCotasData; clientName?: string }
  | { tipo: 'tabela_comparativa'; tabela: TabelaComparativaData; clientName?: string };

type ContactResult = {
  id: number;
  name: string;
  phone?: string | null;
  email?: string | null;
};

type PdfCardState = {
  action: PdfAction;
  contactSearch: string;
  contactResults: ContactResult[];
  selectedContact: ContactResult | null;
  isSearching: boolean;
  isGenerating: boolean;
  isSaved: boolean;
};

type UserProfile = {
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  crmUserId?: number | null;
};

const INITIAL_MESSAGES: Message[] = [
  { id: 1, role: 'assistant', content: 'Olá. Sou o North IA. Como posso ajudar?' },
];

const CHAVE_PROSPECCAO = 'north.ia.prospeccao';
// Aviso só na tela (não vai para o banco): o botão muda o papel da IA a partir
// da próxima mensagem, e sem isso parecia que não fazia nada.
const AVISO_PROSPECCAO_LIGADA = [
  '**Modo prospecção ativado.** A partir da próxima mensagem, o North IA atua como coach de prospecção. Opções:',
  '* Pedir sugestões de quem abordar hoje, com base na carteira.',
  '* Descrever um lead (idade, profissão, objetivo) e receber o perfil, o produto mais provável e a primeira mensagem.',
  '* Colar a resposta do cliente (ex.: "achou a parcela alta") e receber orientação para contornar a objeção.',
  '* Pedir um roteiro de ligação ou de WhatsApp.',
  '',
  'Para voltar ao chat normal, clique novamente em **Prospecção ligada**.',
].join('\n');
const AVISO_PROSPECCAO_DESLIGADA = '**Modo prospecção desativado.**';
const CHAT_MIN_DELAY_MS = 800;
const PDF_ACTION_RE = /\[PDF_ACTION_START\]([A-Za-z0-9+/=%-]+)\[PDF_ACTION_END\]/;

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

function decodePdfAction(encoded: string): PdfAction | null {
  try {
    return JSON.parse(decodeURIComponent(atob(encoded))) as PdfAction;
  } catch {
    return null;
  }
}

function stripPdfMarker(text: string): string {
  return text.replace(PDF_ACTION_RE, '').trim();
}

const normalizeAssistantText = (text: string) => {
  return String(text || '')
    .replace(/\r\n/g, '\n')
    .replace(/\s(#{1,6}\s)/g, '\n\n$1')
    .replace(/\s\*\s(?=\*\*|[\p{L}\p{N}])/gu, '\n* ')
    .trim();
};

const renderInlineMarkdown = (line: string) => {
  const chunks = line.split(/(\*\*[^*]+\*\*)/g);
  return chunks.map((chunk, index) => {
    if (chunk.startsWith('**') && chunk.endsWith('**')) {
      return <strong key={`b-${index}`}>{chunk.slice(2, -2)}</strong>;
    }
    return <Fragment key={`t-${index}`}>{comLinks(chunk, `t-${index}`)}</Fragment>;
  });
};

const isMarkdownTableLine = (line: string) => {
  const trimmed = line.trim();
  return trimmed.startsWith('|') && trimmed.endsWith('|') && trimmed.split('|').length >= 4;
};

const isMarkdownTableSeparator = (line: string) => {
  const trimmed = line.trim();
  return /^\|(?:\s*:?-+:?\s*\|)+$/.test(trimmed);
};

const splitMarkdownTableCells = (line: string) => {
  return line
    .trim()
    .slice(1, -1)
    .split('|')
    .map(cell => cell.trim());
};

const renderMarkdownTable = (
  tableLines: string[],
  key: string,
  onOptionClick?: (rowIndex: number, headers: string[], cells: string[]) => void,
  onGenerateProposal?: () => void,
) => {
  if (tableLines.length < 2) return null;

  const header = splitMarkdownTableCells(tableLines[0]);
  const bodyLines = isMarkdownTableSeparator(tableLines[1])
    ? tableLines.slice(2)
    : tableLines.slice(1);
  const rows = bodyLines
    .filter(line => isMarkdownTableLine(line))
    .map(splitMarkdownTableCells)
    .filter(row => row.length === header.length);

  if (header.length === 0 || rows.length === 0) return null;

  const cleanHeader = header.map(h => h.replace(/\*\*/g, '').trim());
  // Result tables come from a simulation/projeto_estruturado tool call. They show
  // computed fields like Lance pago, Crédito liberado, Lance Embutido, Crédito Líquido,
  // Parcela Pós ou Saldo Devedor. Per-row Simular doesn't apply here — show a single
  // "Gerar PDF" action below the table instead.
  const isResultTable = cleanHeader.some(h => /lance\s*pago|cr[eé]dito\s*liberado|lance\s*embutido|cr[eé]dito\s*l[ií]quido|parcela\s*p[oó]s|saldo\s*devedor/i.test(h));
  const isConsortiumTable = onOptionClick != null
    && !isResultTable
    && cleanHeader.some(h => /^grupo$/i.test(h))
    && cleanHeader.some(h => /^categoria$/i.test(h));

  return (
    <div className="ai-md-table-wrap" key={key}>
      <table className="ai-md-table">
        <thead>
          <tr>
            {header.map((cell, index) => (
              <th key={`th-${index}`}>{renderInlineMarkdown(cell)}</th>
            ))}
            {isConsortiumTable && <th className="ai-md-table-action-th"></th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={`tr-${rowIndex}`}>
              {row.map((cell, cellIndex) => (
                <td key={`td-${rowIndex}-${cellIndex}`}>{renderInlineMarkdown(cell)}</td>
              ))}
              {isConsortiumTable && (
                <td className="ai-md-table-action-td">
                  <button
                    type="button"
                    className="ai-md-table-sim-btn"
                    onClick={() => onOptionClick!(rowIndex, cleanHeader, row)}
                  >
                    Simular
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {isResultTable && onGenerateProposal && (
        <div className="ai-md-table-proposal-wrap">
          <button
            type="button"
            className="ai-md-table-sim-btn ai-md-table-proposal-btn"
            onClick={onGenerateProposal}
          >
            Gerar PDF da proposta
          </button>
        </div>
      )}
    </div>
  );
};

const renderAssistantMessage = (
  content: string,
  onOptionClick?: (rowIndex: number, headers: string[], cells: string[]) => void,
  onGenerateProposal?: () => void,
): ReactNode => {
  const lines = normalizeAssistantText(content).split('\n');
  const nodes: ReactNode[] = [];
  let listBuffer: ReactNode[] = [];
  let tableBuffer: string[] = [];

  const flushList = () => {
    if (listBuffer.length > 0) {
      nodes.push(
        <ul className="ai-md-list" key={`ul-${nodes.length}`}>
          {listBuffer}
        </ul>,
      );
      listBuffer = [];
    }
  };

  const flushTable = () => {
    if (tableBuffer.length > 0) {
      const rendered = renderMarkdownTable(tableBuffer, `table-${nodes.length}`, onOptionClick, onGenerateProposal);
      if (rendered) {
        nodes.push(rendered);
      } else {
        tableBuffer.forEach((line, index) => {
          nodes.push(
            <p className="ai-md-p" key={`table-fallback-${nodes.length}-${index}`}>
              {renderInlineMarkdown(line)}
            </p>,
          );
        });
      }
      tableBuffer = [];
    }
  };

  lines.forEach((rawLine, lineIndex) => {
    const line = rawLine.trim();
    if (!line) {
      flushList();
      flushTable();
      return;
    }

    if (isMarkdownTableLine(line) || (tableBuffer.length > 0 && isMarkdownTableSeparator(line))) {
      flushList();
      tableBuffer.push(line);
      return;
    }

    flushTable();

    if (line.startsWith('* ')) {
      listBuffer.push(
        <li key={`li-${lineIndex}`}>{renderInlineMarkdown(line.slice(2).trim())}</li>,
      );
      return;
    }

    flushList();

    if (line.startsWith('### ')) {
      nodes.push(
        <h3 className="ai-md-h3" key={`h3-${lineIndex}`}>
          {renderInlineMarkdown(line.slice(4).trim())}
        </h3>,
      );
      return;
    }

    nodes.push(
      <p className="ai-md-p" key={`p-${lineIndex}`}>
        {renderInlineMarkdown(line)}
      </p>,
    );
  });

  flushList();
  flushTable();

  return <div className="ai-markdown">{nodes}</div>;
};

export const AIPage = () => {
  const { collapsed, isMobile } = useSidebar();
  const sidebarW = isMobile ? 0 : (collapsed ? 80 : 256);
  const [messages, setMessages] = useState<Message[]>(INITIAL_MESSAGES);
  const [prompt, setPrompt] = useState('');
  const [lastSentPrompt, setLastSentPrompt] = useState<string>('');
  const [isThinking, setIsThinking] = useState(false);
  const [nextId, setNextId] = useState(INITIAL_MESSAGES.length + 1);
  const [conversationId, setConversationId] = useState<string | undefined>(undefined);
  const [conversasAbertas, setConversasAbertas] = useState(false);
  // Muda a cada resposta: a lista de conversas reordena sem reabrir o painel.
  const [versaoConversas, setVersaoConversas] = useState(0);
  const [isHydratingHistory, setIsHydratingHistory] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  // PDF state: one card per message id
  const [pdfCards, setPdfCards] = useState<Record<number, PdfCardState>>({});
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [ajudaAberta, setAjudaAberta] = useState(false);
  // Modo prospecção: o mesmo chat, com a North IA no papel de coach de
  // prospecção (chat-ai/playbookPrompt.ts). Lembrado neste navegador.
  const [prospeccao, setProspeccao] = useState<boolean>(() => {
    try {
      return localStorage.getItem(CHAVE_PROSPECCAO) === '1';
    } catch {
      return false;
    }
  });
  const alternarProspeccao = () => {
    const ligar = !prospeccao;
    setProspeccao(ligar);
    const id = nextId;
    setNextId(id + 1);
    setMessages(prev => [...prev, {
      id,
      role: 'assistant',
      content: ligar ? AVISO_PROSPECCAO_LIGADA : AVISO_PROSPECCAO_DESLIGADA,
    }]);
    try {
      localStorage.setItem(CHAVE_PROSPECCAO, ligar ? '1' : '0');
    } catch {
      // sem memória local: vale só nesta tela
    }
  };
  const [porQue, setPorQue] = useState<{ payload: ProposalPayload; userInfo: UserInfo } | null>(null);
  const contactSearchTimers = useRef<Record<number, ReturnType<typeof setTimeout>>>({});

  // Load user profile once
  useEffect(() => {
    const loadProfile = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data: row } = await supabase
        .from('users')
        .select('name,email,phone,id')
        .eq('user_id', user.id)
        .maybeSingle();
      if (row) {
        setUserProfile({
          name: row.name ?? user.email,
          email: row.email ?? user.email,
          phone: row.phone ?? null,
          crmUserId: typeof row.id === 'number' ? row.id : null,
        });
      } else {
        setUserProfile({ name: user.email, email: user.email });
      }
    };
    void loadProfile();
  }, []);

  /**
   * Abre uma conversa: a pedida, ou a mais recente quando `id` não vem (é
   * como a tela abre). `ativo` protege de resposta que chega depois de a
   * pessoa já ter trocado de conversa ou saído da tela.
   */
  const abrirConversa = useCallback(async (id?: string, ativo: () => boolean = () => true) => {
    setIsHydratingHistory(true);
    try {
      let alvo = id;
      if (!alvo) {
        // Filtro por user_id obrigatório: desde a migration 223 o super admin
        // LÊ as conversas de todos (Auditoria) e, sem ele, abriria a de outro.
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        const { data: conversation, error: conversationError } = await supabase
          .from('chat_conversations')
          .select('id')
          .eq('user_id', user.id)
          .order('last_message_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        if (conversationError || !conversation?.id) return;
        alvo = conversation.id as string;
      }

      const { data: messageRows, error: messageError } = await supabase
        .from('chat_messages')
        .select('role,content,created_at')
        .eq('conversation_id', alvo)
        .order('created_at', { ascending: true })
        .limit(150);

      if (messageError || !ativo()) return;

      const hydrated = (messageRows ?? [])
        .filter((row: any) => row.role === 'user' || row.role === 'assistant')
        .map((row: any, index: number) => ({
          id: index + 1,
          role: row.role as 'user' | 'assistant',
          content: stripPdfMarker(String(row.content || '')),
        }));

      setMessages(hydrated.length > 0 ? hydrated : INITIAL_MESSAGES);
      setNextId((hydrated.length > 0 ? hydrated.length : INITIAL_MESSAGES.length) + 1);
      setConversationId(alvo);
      setPdfCards({});
    } finally {
      if (ativo()) setIsHydratingHistory(false);
    }
  }, []);

  useEffect(() => {
    let active = true;
    void abrirConversa(undefined, () => active);
    return () => {
      active = false;
    };
  }, [abrirConversa]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, isThinking]);

  const searchContacts = useCallback(async (msgId: number, query: string) => {
    if (query.length < 2) {
      setPdfCards(prev => {
        const card = prev[msgId];
        if (!card) return prev;
        return { ...prev, [msgId]: { ...card, contactResults: [], isSearching: false } };
      });
      return;
    }
    setPdfCards(prev => {
      const card = prev[msgId];
      if (!card) return prev;
      return { ...prev, [msgId]: { ...card, isSearching: true } };
    });
    const { data } = await supabase
      .from('contacts')
      .select('id,details')
      .ilike('details->>name', `%${query}%`)
      .limit(20);
    const results: ContactResult[] = (data ?? []).map((row: any) => ({
      id: row.id,
      name: String(row.details?.name || `Contato ${row.id}`),
      phone: row.details?.phone ?? null,
      email: row.details?.email ?? null,
    }));
    setPdfCards(prev => {
      const card = prev[msgId];
      if (!card) return prev;
      return { ...prev, [msgId]: { ...card, contactResults: results, isSearching: false } };
    });
  }, []);

  const handleContactSearchChange = useCallback((msgId: number, value: string) => {
    setPdfCards(prev => {
      const card = prev[msgId];
      if (!card) return prev;
      return { ...prev, [msgId]: { ...card, contactSearch: value, selectedContact: null } };
    });
    clearTimeout(contactSearchTimers.current[msgId]);
    contactSearchTimers.current[msgId] = setTimeout(() => {
      void searchContacts(msgId, value);
    }, 300);
  }, [searchContacts]);

  const handleContactSelect = useCallback((msgId: number, contact: ContactResult) => {
    setPdfCards(prev => {
      const card = prev[msgId];
      if (!card) return prev;
      return {
        ...prev,
        [msgId]: {
          ...card,
          selectedContact: contact,
          contactSearch: contact.name,
          contactResults: [],
        },
      };
    });
  }, []);

  const handleDownloadPdf = useCallback(async (msgId: number) => {
    const card = pdfCards[msgId];
    if (!card || card.isGenerating) return;

    setPdfCards(prev => ({ ...prev, [msgId]: { ...prev[msgId], isGenerating: true } }));
    try {
      const uInfo = {
        name: userProfile?.name ?? undefined,
        email: userProfile?.email ?? undefined,
        phone: userProfile?.phone ?? undefined,
      };
      const nomeCliente = card.selectedContact?.name ?? card.action.clientName;

      // O que foi para o PDF é o que fica salvo. Se divergirem, "Gerar
      // novamente" na tela de propostas devolve um documento diferente do que
      // o cliente recebeu.
      let payloadSalvo: Record<string, unknown>;
      let tipoSalvo: PdfAction['tipo'];

      if (card.action.tipo === 'tabela_comparativa') {
        const tabela = { ...card.action.tabela, clientName: nomeCliente };
        await generateTabelaComparativaPdf(tabela, uInfo);
        payloadSalvo = tabela;
        tipoSalvo = 'tabela_comparativa';
      } else if (escolherModeloProposta(card.action.projeto) === 'venda_direta') {
        const proposta = converterParaPropostaDireta(card.action.projeto, nomeCliente);
        await generateProposalPdf(proposta, uInfo);
        // Venda direta: oferece o PDF "Por que consórcio" (a caixinha decide).
        setPorQue({ payload: proposta, userInfo: uInfo });
        payloadSalvo = proposta;
        tipoSalvo = 'cota_unica';
      } else {
        const pdfData: MultiCotasData = { ...card.action.projeto, clientName: nomeCliente };
        await generateMultiCotasPdf(pdfData, uInfo);
        payloadSalvo = pdfData;
        tipoSalvo = 'projeto_estruturado';
      }

      // Save to proposal_simulations if contact selected
      if (card.selectedContact) {
        await supabase.from('proposal_simulations').insert({
          contact_id: card.selectedContact.id,
          sales_person_id: userProfile?.crmUserId ?? null,
          generated_at: new Date().toISOString(),
          // Grupo só na venda direta: no projeto estruturado as cotas podem vir
          // de grupos diferentes, e gravar um deles fingiria que o projeto é de
          // um grupo só.
          group_number: tipoSalvo === 'cota_unica' && typeof payloadSalvo.groupNumber === 'number'
            ? payloadSalvo.groupNumber
            : null,
          // O payload guardado é o conteúdo do PDF, qualquer que seja o formato:
          // projeto (cotas que somam), proposta de cota única ou tabela de
          // opções. O `simulation_input.tipo` logo abaixo diz qual dos três é.
          proposal_payload: {
            ...payloadSalvo,
            clientName: card.selectedContact.name,
            clientPhone: card.selectedContact.phone ?? null,
            clientEmail: card.selectedContact.email ?? null,
          },
          simulation_input: { tipo: tipoSalvo },
          contact_snapshot: {
            id: card.selectedContact.id,
            name: card.selectedContact.name,
            email: card.selectedContact.email ?? null,
            phone: card.selectedContact.phone ?? null,
          },
          advisor_snapshot: uInfo,
        });
        setPdfCards(prev => ({ ...prev, [msgId]: { ...prev[msgId], isSaved: true, isGenerating: false } }));
      } else {
        setPdfCards(prev => ({ ...prev, [msgId]: { ...prev[msgId], isGenerating: false } }));
      }
    } catch (err) {
      console.error('PDF generation error:', err);
      setPdfCards(prev => ({ ...prev, [msgId]: { ...prev[msgId], isGenerating: false } }));
    }
  }, [pdfCards, userProfile]);

  const sendPrompt = async (value: string) => {
    const text = value.trim();
    if (!text || isThinking || isHydratingHistory) return;
    // Microfone ligado ou transcrição final no ar: vai o que está no campo.
    if (ditado.ouvindo || ditado.transcrevendo) ditado.descartar();

    const uid = nextId;
    const aid = uid + 1;
    setMessages(prev => [...prev, { id: uid, role: 'user', content: text }]);
    setNextId(aid + 1);
    setPrompt('');
    setLastSentPrompt(text);
    setIsThinking(true);

    try {
      const [response] = await Promise.all([
        sendChatMessage({ message: text, conversationId, modoCoach: prospeccao ? 'prospeccao' : undefined }),
        sleep(CHAT_MIN_DELAY_MS),
      ]);
      setConversationId(response.conversationId);
      setVersaoConversas(v => v + 1);

      const rawAnswer = response.answer;
      const match = PDF_ACTION_RE.exec(rawAnswer);
      const cleanAnswer = stripPdfMarker(rawAnswer);

      setMessages(prev => [...prev, { id: aid, role: 'assistant', content: cleanAnswer }]);
      setLastSentPrompt('');

      if (match) {
        const action = decodePdfAction(match[1]);
        if (action) {
          setPdfCards(prev => ({
            ...prev,
            [aid]: {
              action,
              contactSearch: action.clientName ?? '',
              contactResults: [],
              selectedContact: null,
              isSearching: false,
              isGenerating: false,
              isSaved: false,
            },
          }));
        }
      }
    } catch (error: any) {
      const rawMessage = String(error?.message || 'falha desconhecida');
      const unauthorized = /401|unauthorized|nao autenticado|auth/i.test(rawMessage.toLowerCase());
      const forbidden = /403|forbidden|permissao|proibido/i.test(rawMessage.toLowerCase());
      const rateLimited = /429|rate limit|too many/i.test(rawMessage.toLowerCase());

      let errorReply = `Erro ao consultar o North IA: ${rawMessage}`;
      if (unauthorized) errorReply = 'Sessão expirada. Faça login novamente.';
      else if (forbidden) errorReply = 'Sem permissão para consultar esses dados.';
      else if (rateLimited) errorReply = 'Limite de requisições atingido. Aguarde alguns segundos e tente novamente.';

      setMessages(prev => [...prev, { id: aid, role: 'assistant', content: errorReply }]);
    } finally {
      setIsThinking(false);
    }
  };

  // Falar em vez de digitar: o texto aparece no campo e vai ao parar.
  const ditado = useDitado({ aoMudarTexto: setPrompt });

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void sendPrompt(prompt); }
  };

  const handleStartNewConversation = () => {
    if (isThinking) return;
    setConversationId(undefined);
    setMessages(INITIAL_MESSAGES);
    setNextId(INITIAL_MESSAGES.length + 1);
    setPrompt('');
    setPdfCards({});
  };

  const renderPdfCard = (msgId: number) => {
    const card = pdfCards[msgId];
    if (!card) return null;

    // O cabeçalho tem que dizer o mesmo que o botão vai baixar — antes ele
    // anunciava "Projeto Estruturado" até para tabela comparativa.
    const rotulo = card.action.tipo === 'tabela_comparativa'
      ? 'Tabela Comparativa'
      : escolherModeloProposta(card.action.projeto) === 'venda_direta'
        ? 'Venda Direta'
        : 'Projeto Estruturado';

    return (
      <div className="ai-pdf-card">
        <div className="ai-pdf-card-header">
          <FileText style={{ width: 15, height: 15 }} />
          <span>Proposta — {rotulo}</span>
          <button
            type="button"
            className="ai-pdf-card-close"
            onClick={() => setPdfCards(prev => { const n = { ...prev }; delete n[msgId]; return n; })}
            title="Fechar"
          >
            <X style={{ width: 13, height: 13 }} />
          </button>
        </div>

        {card.isSaved ? (
          <div className="ai-pdf-saved">
            ✓ PDF gerado e proposta salva para <strong>{card.selectedContact?.name}</strong>
          </div>
        ) : (
          <>
            <div className="ai-pdf-contact-label">Vincular a um contato (opcional)</div>
            {card.selectedContact ? (
              <div className="ai-pdf-contact-selected">
                <span>{card.selectedContact.name}</span>
                <button
                  type="button"
                  className="ai-pdf-contact-clear"
                  onClick={() => setPdfCards(prev => ({
                    ...prev,
                    [msgId]: { ...prev[msgId], selectedContact: null, contactSearch: '' },
                  }))}
                >
                  Trocar
                </button>
              </div>
            ) : (
              <div className="ai-pdf-search-wrap">
                <input
                  className="ai-pdf-search"
                  type="text"
                  placeholder="Buscar contato pelo nome..."
                  value={card.contactSearch}
                  onChange={e => handleContactSearchChange(msgId, e.target.value)}
                  autoComplete="off"
                />
                {card.isSearching && (
                  <div className="ai-pdf-search-results">
                    <div className="ai-pdf-search-item" style={{ opacity: 0.5 }}>Buscando...</div>
                  </div>
                )}
                {!card.isSearching && card.contactResults.length > 0 && (
                  <div className="ai-pdf-search-results">
                    {card.contactResults.map(c => (
                      <button
                        key={c.id}
                        type="button"
                        className="ai-pdf-search-item"
                        onClick={() => handleContactSelect(msgId, c)}
                      >
                        {c.name}
                        {c.phone && <span className="ai-pdf-contact-detail"> · {c.phone}</span>}
                      </button>
                    ))}
                  </div>
                )}
                {!card.isSearching && card.contactSearch.length >= 2 && card.contactResults.length === 0 && (
                  <div className="ai-pdf-search-results">
                    <div className="ai-pdf-search-item" style={{ opacity: 0.5 }}>Nenhum contato encontrado.</div>
                  </div>
                )}
              </div>
            )}

            <button
              type="button"
              className="ai-pdf-download-btn"
              disabled={card.isGenerating}
              onClick={() => handleDownloadPdf(msgId)}
            >
              <Download style={{ width: 14, height: 14 }} />
              {card.isGenerating ? 'Gerando PDF...' : 'Baixar PDF'}
            </button>
          </>
        )}
      </div>
    );
  };

  return (
    <>
      <PorQueConsorcioDialog
        payload={porQue?.payload ?? null}
        userInfo={porQue?.userInfo ?? {}}
        onClose={() => setPorQue(null)}
      />
      <style>{`
        /* ── página inteira ── */
        /* Abaixo da barra superior do Layout (h-14), que fica por cima e
           tapava o cabeçalho com Conversas e Nova conversa. */
        .ai-shell {
          position: fixed;
          inset: 3.5rem 0 0 0;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          background: #0E1619;
          color: #f0f4f8;
          transition: background 0.3s, color 0.3s;
        }
        .light .ai-shell {
          background: var(--pagina);
          color: #0e1619;
        }
        /* App instalado: a barra desce o tamanho do entalhe (index.css). */
        @media (display-mode: standalone) {
          .ai-shell { top: calc(3.5rem + env(safe-area-inset-top)); }
        }

        .ai-orb-bg {
          position: absolute;
          inset: 0;
          pointer-events: none;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .ai-container {
          position: relative;
          width: 220px;
          height: 220px;
          background: #0E1619;
          filter: blur(16px) contrast(5.5);
           mix-blend-mode: screen;
           opacity: 0.45;
          transition: opacity 0.3s ease;
        }

        .orb {
          position: absolute;
          width: 108px;
          height: 108px;
          border-radius: 50%;
          top: 56px;
          left: 56px;
          background: #152844;
          animation: move 4.2s infinite alternate ease-in-out;
        }

        .orb.blue {
          background: #1e3f6e;
          animation-delay: -2s;
        }

        /* Dourado da marca. O nome da classe segue a cor, não o histórico:
           era "green" de um tema antigo. */
        .orb.dourado {
          background: #8a7455;
          animation-delay: -1s;
        }

        @keyframes move {
          from { transform: translate(-30px, -30px) scale(1); }
          to { transform: translate(30px, 30px) scale(1.22); }
        }

        .ai-orb-bg.thinking .ai-container {
          opacity: 0.95;
        }

        .ai-orb-bg.thinking .orb {
          animation-duration: 1.05s;
        }

        .light .ai-container {
          background: transparent;
          opacity: 0.48;
          filter: blur(14px) saturate(1.06);
          -webkit-mask-image: none;
          mask-image: none;
          mix-blend-mode: multiply;
        }

        .light .orb { background: #5f7ca7; }
        .light .orb.blue { background: #8fcbe6; }
        .light .orb.dourado { background: #c4ae90; }
        .light .ai-orb-bg.thinking .ai-container { opacity: 0.62; }

        /* ── área de mensagens ── */
        .ai-messages {
          flex: 1;
          overflow-y: auto;
          padding: 28px 20px 16px;
          position: relative;
          z-index: 1;
          display: flex;
          flex-direction: column;
          gap: 14px;
        }
        .ai-messages::-webkit-scrollbar { width: 7px; }
        .ai-messages::-webkit-scrollbar-thumb {
          background: rgba(255,255,255,0.12);
          border-radius: 999px;
        }
        .light .ai-messages::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.12); }

        .bubble {
          max-width: 75%;
          padding: 12px 18px;
          border-radius: 22px;
          font-size: 0.9rem;
          line-height: 1.65;
        }
        .bubble-ai {
          align-self: flex-start;
          border-radius: 22px 22px 22px 6px;
          background: rgba(255,255,255,0.08);
          border: 1px solid rgba(255,255,255,0.12);
          color: #e8f0ea;
          backdrop-filter: blur(8px);
        }
        .light .bubble-ai {
          background: #ffffff;
          border: 1px solid rgba(53,70,96,0.12);
          color: #0e1619;
        }
        .bubble-user {
          align-self: flex-end;
          border-radius: 22px 22px 6px 22px;
          background: #1F2C40;
          color: #ffffff;
          box-shadow: 0 4px 16px rgba(31,44,64,0.28);
        }
        .bubble-sender {
          font-size: 0.68rem;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          opacity: 0.45;
          margin-bottom: 4px;
        }

        .ai-markdown { display: flex; flex-direction: column; gap: 0.45rem; }
        .ai-md-p { margin: 0; white-space: pre-wrap; }
        .ai-md-h3 { margin: 0.35rem 0 0.15rem; font-size: 0.98rem; font-weight: 700; }
        .ai-md-list { margin: 0; padding-left: 1.05rem; display: grid; gap: 0.22rem; }
        .ai-md-list li { margin: 0; white-space: pre-wrap; }

        .ai-md-table-wrap {
          width: 100%;
          overflow-x: auto;
          border-radius: 14px;
          border: 1px solid rgba(255,255,255,0.1);
          background: rgba(255,255,255,0.03);
        }
        .light .ai-md-table-wrap {
          border: 1px solid rgba(53,70,96,0.1);
          background: #ffffff;
        }
        .ai-md-table { width: 100%; border-collapse: collapse; font-size: 0.86rem; min-width: 420px; }
        .ai-md-table th, .ai-md-table td {
          padding: 0.65rem 0.8rem;
          text-align: left;
          vertical-align: top;
          border-bottom: 1px solid rgba(255,255,255,0.08);
        }
        .light .ai-md-table th, .light .ai-md-table td { border-bottom: 1px solid rgba(53,70,96,0.09); }
        .ai-md-table thead th {
          font-size: 0.75rem;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: rgba(255,255,255,0.72);
          background: rgba(255,255,255,0.05);
        }
        .light .ai-md-table thead th { color: #8a929c; background: rgba(53,70,96,0.04); }
        .ai-md-table tbody tr:last-child td { border-bottom: none; }
        .ai-md-table-action-th {
          width: 80px;
          min-width: 80px;
          background: rgba(255,255,255,0.05);
        }
        .light .ai-md-table-action-th { background: rgba(53,70,96,0.04); }
        .ai-md-table-action-td {
          width: 80px;
          min-width: 80px;
          vertical-align: middle;
          padding: 0.4rem 0.6rem;
        }
        .ai-md-table-sim-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 4px 10px;
          border-radius: 8px;
          border: 1px solid rgba(114,232,160,0.35);
          background: rgba(114,232,160,0.1);
          color: #72e8a0;
          font-size: 0.72rem;
          font-weight: 600;
          cursor: pointer;
          white-space: nowrap;
          transition: background 0.15s, opacity 0.15s;
        }
        .ai-md-table-sim-btn:hover {
          background: rgba(114,232,160,0.22);
        }
        .light .ai-md-table-sim-btn {
          border-color: rgba(31,44,64,0.22);
          background: rgba(31,44,64,0.05);
          color: #1F2C40;
        }
        .light .ai-md-table-sim-btn:hover { background: rgba(31,44,64,0.1); }
        .ai-md-table-proposal-wrap {
          display: flex;
          justify-content: flex-end;
          padding: 0.5rem 0.6rem 0.6rem;
        }
        .ai-md-table-proposal-btn {
          font-size: 0.78rem;
          padding: 6px 14px;
        }

        /* ── PDF Card ── */
        .ai-pdf-card {
          align-self: flex-start;
          max-width: 75%;
          width: 340px;
          background: rgba(28,56,97,0.18);
          border: 1px solid rgba(114,232,160,0.22);
          border-radius: 16px;
          padding: 14px 16px;
          font-size: 0.84rem;
          backdrop-filter: blur(8px);
        }
        .light .ai-pdf-card {
          background: rgba(28,56,97,0.07);
          border-color: rgba(28,56,97,0.18);
        }
        .ai-pdf-card-header {
          display: flex;
          align-items: center;
          gap: 7px;
          font-weight: 600;
          font-size: 0.82rem;
          color: #72e8a0;
          margin-bottom: 10px;
        }
        .light .ai-pdf-card-header { color: #1F2C40; }
        .ai-pdf-card-close {
          margin-left: auto;
          background: none;
          border: none;
          cursor: pointer;
          color: inherit;
          opacity: 0.5;
          display: flex;
          align-items: center;
          padding: 2px;
        }
        .ai-pdf-card-close:hover { opacity: 1; }
        .ai-pdf-contact-label {
          font-size: 0.75rem;
          opacity: 0.6;
          margin-bottom: 6px;
        }
        .ai-pdf-search-wrap { position: relative; }
        .ai-pdf-search {
          width: 100%;
          border-radius: 10px;
          border: 1px solid rgba(255,255,255,0.16);
          background: rgba(255,255,255,0.07);
          color: inherit;
          padding: 8px 12px;
          font-size: 0.82rem;
          outline: none;
          box-sizing: border-box;
        }
        .ai-pdf-search:focus { border-color: rgba(114,232,160,0.45); }
        .light .ai-pdf-search {
          border-color: rgba(53,70,96,0.2);
          background: rgba(0,0,0,0.04);
        }
        .ai-pdf-search-results {
          position: absolute;
          top: calc(100% + 4px);
          left: 0;
          right: 0;
          background: #1a2535;
          border: 1px solid rgba(255,255,255,0.12);
          border-radius: 10px;
          max-height: 180px;
          overflow-y: auto;
          z-index: 10;
          box-shadow: 0 8px 24px rgba(0,0,0,0.4);
        }
        .light .ai-pdf-search-results {
          background: #fff;
          border-color: rgba(0,0,0,0.1);
          box-shadow: 0 8px 24px rgba(0,0,0,0.12);
        }
        .ai-pdf-search-item {
          display: block;
          width: 100%;
          text-align: left;
          padding: 8px 12px;
          font-size: 0.8rem;
          background: none;
          border: none;
          cursor: pointer;
          color: inherit;
          border-bottom: 1px solid rgba(255,255,255,0.06);
        }
        .ai-pdf-search-item:last-child { border-bottom: none; }
        .ai-pdf-search-item:hover { background: rgba(255,255,255,0.07); }
        .light .ai-pdf-search-item:hover { background: rgba(0,0,0,0.05); }
        .ai-pdf-contact-detail { opacity: 0.55; font-size: 0.75rem; }
        .ai-pdf-contact-selected {
          display: flex;
          align-items: center;
          justify-content: space-between;
          background: rgba(255,255,255,0.06);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 10px;
          padding: 8px 12px;
          font-size: 0.82rem;
          margin-bottom: 4px;
        }
        .light .ai-pdf-contact-selected { background: rgba(0,0,0,0.04); border-color: rgba(0,0,0,0.08); }
        .ai-pdf-contact-clear {
          background: none;
          border: none;
          cursor: pointer;
          color: inherit;
          opacity: 0.5;
          font-size: 0.75rem;
        }
        .ai-pdf-contact-clear:hover { opacity: 1; }
        .ai-pdf-download-btn {
          margin-top: 10px;
          width: 100%;
          height: 38px;
          border-radius: 10px;
          border: none;
          cursor: pointer;
          font-weight: 600;
          font-size: 0.82rem;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 7px;
          background: radial-gradient(circle at 85% 10%, #2c3e58 0%, #1F2C40 55%, #0F1A24 100%);
          color: #ffffff;
          transition: opacity 0.2s;
        }
        .ai-pdf-download-btn:hover:not(:disabled) { opacity: 0.88; }
        .ai-pdf-download-btn:disabled { opacity: 0.45; cursor: not-allowed; }
        .ai-pdf-saved {
          font-size: 0.8rem;
          color: #72e8a0;
          padding: 6px 0 2px;
        }
        .light .ai-pdf-saved { color: #1c6240; }

        /* dots de loading */
        .dot-loading span {
          display: inline-block;
          width: 8px; height: 8px;
          border-radius: 50%;
          background: #72e8a0;
          animation: dot-pulse 1.1s ease-in-out infinite;
        }
        .dot-loading span:nth-child(2) { animation-delay: 0.18s; }
        .dot-loading span:nth-child(3) { animation-delay: 0.36s; }
        @keyframes dot-pulse {
          0%, 80%, 100% { transform: scale(0.7); opacity: 0.4; }
          40%            { transform: scale(1.1); opacity: 1;   }
        }

        /* ── input bar ── */
        .ai-input-bar {
          position: relative;
          z-index: 1;
          padding: 12px 16px 20px;
          border-top: 1px solid rgba(255,255,255,0.08);
          background: rgba(0,0,0,0.25);
          backdrop-filter: blur(12px);
        }
        .light .ai-input-bar {
          border-top: 1px solid rgba(0,0,0,0.08);
          background: rgba(255,255,255,0.6);
        }
        .ai-input-row { display: flex; align-items: flex-end; gap: 10px; }
        .ai-textarea {
          flex: 1;
          resize: none;
          border-radius: 18px;
          padding: 12px 16px;
          font-size: 0.9rem;
          line-height: 1.5;
          min-height: 52px;
          max-height: 180px;
          outline: none;
          border: 1px solid rgba(255,255,255,0.14);
          background: rgba(255,255,255,0.06);
          color: inherit;
          transition: border-color 0.2s;
        }
        .ai-textarea::placeholder { opacity: 0.38; }
        .ai-textarea:focus { border-color: rgba(114,232,160,0.5); }
        .light .ai-textarea { border: 1px solid rgba(0,0,0,0.13); background: rgba(0,0,0,0.04); }
        .light .ai-textarea:focus { border-color: rgba(31,44,64,0.45); }

        .ai-send-btn {
          flex-shrink: 0;
          height: 52px;
          padding: 0 22px;
          border-radius: 16px;
          border: none;
          cursor: pointer;
          font-weight: 600;
          font-size: 0.875rem;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: radial-gradient(circle at 85% 10%, #2c3e58 0%, #1F2C40 55%, #0F1A24 100%);
          color: #ffffff;
          transition: opacity 0.2s, transform 0.15s;
        }
        .ai-send-btn:hover:not(:disabled) { opacity: 0.9; transform: translateY(-1px); }
        .ai-send-btn:disabled { opacity: 0.38; cursor: not-allowed; }

        .ai-hint {
          margin-top: 6px;
          font-size: 0.7rem;
          opacity: 0.35;
          display: flex;
          align-items: center;
          gap: 5px;
          padding-left: 4px;
        }

        /* header compacto */
        .ai-header {
          position: relative;
          z-index: 1;
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 14px 20px 12px;
          border-bottom: 1px solid rgba(255,255,255,0.07);
          background: rgba(0,0,0,0.2);
          backdrop-filter: blur(12px);
        }
        .light .ai-header {
          border-bottom: 1px solid rgba(0,0,0,0.07);
          background: rgba(255,255,255,0.55);
        }
        .ai-header-badge {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          background: rgba(114,232,160,0.15);
          border: 1px solid rgba(114,232,160,0.25);
          border-radius: 999px;
          padding: 3px 10px;
          font-size: 0.7rem;
          font-weight: 600;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: #72e8a0;
        }
        .light .ai-header-badge {
          background: #F4ECDD;
          border-color: rgba(196,173,142,0.55);
          color: #7A6440;
        }
        .ai-header-title { font-weight: 700; font-size: 1rem; }
        .ai-status-dot {
          width: 8px; height: 8px;
          border-radius: 50%;
          background: #72e8a0;
          margin-left: auto;
          box-shadow: 0 0 6px rgba(114,232,160,0.7);
        }

        .ai-new-chat-btn {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          margin-left: 10px;
          border: 1px solid rgba(255,255,255,0.16);
          background: rgba(255,255,255,0.04);
          color: inherit;
          border-radius: 999px;
          font-size: 0.72rem;
          letter-spacing: 0.02em;
          padding: 6px 12px;
          cursor: pointer;
          transition: opacity 0.2s ease;
        }
        .ai-new-chat-btn:hover:not(:disabled) { opacity: 0.85; }
        .ai-new-chat-btn:disabled { opacity: 0.45; cursor: not-allowed; }
        .light .ai-new-chat-btn { border-color: rgba(0,0,0,0.13); background: rgba(0,0,0,0.02); }
        .ai-new-chat-btn.ai-modo-ativo,
        .light .ai-new-chat-btn.ai-modo-ativo { border-color: #c4ae90; background: rgba(196,174,144,0.2); }
        /* Celular: os quatro botões não cabem com nome; ficam só os ícones. */
        @media (max-width: 640px) {
          .ai-header { gap: 6px; padding: 10px 12px; }
          .ai-header-title { display: none; }
          .ai-new-chat-btn { margin-left: 0; padding: 0; width: 36px; height: 36px; justify-content: center; }
          .ai-new-chat-btn svg { width: 17px !important; height: 17px !important; }
          .ai-btn-label { display: none; }
        }
      `}</style>

      <div className="ai-shell" style={{ left: sidebarW }}>
        <div className={`ai-orb-bg ${isThinking ? 'thinking' : ''}`} aria-hidden="true">
          <div className="ai-container">
            <div className="orb" />
            <div className="orb blue" />
            <div className="orb dourado" />
          </div>
        </div>

        <div className="ai-header">
          <span className="ai-header-badge">
            <Sparkles style={{ width: 12, height: 12 }} />
            North IA
          </span>
          <span className="ai-header-title">{prospeccao ? 'Prospecção' : 'Chat'}</span>
          <button
            type="button"
            className={`ai-new-chat-btn ${prospeccao ? 'ai-modo-ativo' : ''}`}
            onClick={alternarProspeccao}
            aria-pressed={prospeccao}
            aria-label={prospeccao ? 'Prospecção ligada' : 'Modo prospecção'}
            title={prospeccao
              ? 'Voltar ao chat normal'
              : 'Coach de prospecção: quem abordar, o que dizer e como responder a objeções'}
          >
            <Crosshair style={{ width: 14, height: 14 }} />
            <span className="ai-btn-label">{prospeccao ? 'Prospecção ligada' : 'Modo prospecção'}</span>
          </button>
          <button
            type="button"
            className="ai-new-chat-btn"
            onClick={() => setAjudaAberta(true)}
            title="Recursos do North IA"
            aria-label="Ajuda"
          >
            <HelpCircle style={{ width: 14, height: 14 }} />
            <span className="ai-btn-label">Ajuda</span>
          </button>
          <button
            type="button"
            className="ai-new-chat-btn"
            onClick={() => setConversasAbertas(true)}
            disabled={isThinking}
            title="Conversas anteriores"
            aria-label="Conversas"
          >
            <MessagesSquare style={{ width: 14, height: 14 }} />
            <span className="ai-btn-label">Conversas</span>
          </button>
          <button
            type="button"
            className="ai-new-chat-btn"
            onClick={handleStartNewConversation}
            disabled={isThinking || isHydratingHistory}
            title="Nova conversa"
            aria-label="Nova conversa"
          >
            <SquarePen style={{ width: 14, height: 14 }} />
            <span className="ai-btn-label">Nova conversa</span>
          </button>
          <div className="ai-status-dot" title="Online" />
        </div>

        <div className="ai-messages">
          {messages.map(msg => (
            <Fragment key={msg.id}>
              <div className={`bubble ${msg.role === 'user' ? 'bubble-user' : 'bubble-ai'}`}>
                <div className="bubble-sender">{msg.role === 'user' ? 'Você' : 'North IA'}</div>
                {msg.role === 'assistant'
                  ? renderAssistantMessage(
                      msg.content,
                      isThinking ? undefined : (rowIndex, headers, cells) => {
                        const idx = (k: RegExp) => headers.findIndex(h => k.test(h));
                        const cell = (k: RegExp) => {
                          const i = idx(k);
                          return i >= 0 ? cells[i]?.trim() : '';
                        };
                        const grupo = cell(/^grupo$/i);
                        const cat = cell(/^categoria$/i);
                        const prazo = cell(/^prazo$/i);
                        const creditoCota = cell(/crédito.*cota|cota.*crédito|crédito\/cota|cr.dito/i);
                        // Quantas cotas daquela linha atendem o alvo — a tabela
                        // já traz isso; sem passar adiante, o backend soma cotas
                        // por conta própria e estoura o valor pedido.
                        //
                        // Só o PRIMEIRO número da célula: a IA às vezes escreve
                        // "2 (parcela total de R$ 9.187,50)", e tirar todos os
                        // não-dígitos daria "2918750" cotas.
                        const cotasMatch = cell(/cotas?\s*sugerid/i).match(/\d+/);
                        const cotasNum = cotasMatch ? Number(cotasMatch[0]) : 0;
                        const cotas = cotasNum >= 1 && cotasNum <= 50 ? String(cotasNum) : '';
                        const parts: string[] = [];
                        if (grupo) parts.push(`Grupo ${grupo}`);
                        if (cat) parts.push(`Categoria ${cat}`);
                        if (prazo) parts.push(`prazo ${prazo}`);
                        if (creditoCota) parts.push(`crédito/cota ${creditoCota}`);
                        if (cotas) parts.push(`${cotas} cota${cotas === '1' ? '' : 's'}`);
                        // NÃO pede "crédito líquido": o crédito/cota da tabela é
                        // o valor da carta. Tratar como líquido inflava a
                        // proposta (500k viravam 1M contratado).
                        const text = parts.length > 0
                          ? `Simule o projeto estruturado desta linha: ${parts.join(', ')}. O crédito/cota é o valor bruto da carta; use os percentuais de lance já definidos na conversa.`
                          : `opção ${rowIndex + 1}`;
                        void sendPrompt(text);
                      },
                      isThinking ? undefined : () => { void sendPrompt('Gere a proposta em PDF'); },
                    )
                  : msg.content}
              </div>
              {msg.role === 'assistant' && renderPdfCard(msg.id)}
            </Fragment>
          ))}

          {!isThinking && lastSentPrompt && messages.length > 0 && messages[messages.length - 1].role === 'assistant' && messages[messages.length - 1].content.startsWith('Erro ao consultar') && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '4px 8px' }}>
              <button
                type="button"
                onClick={() => {
                  const retryText = lastSentPrompt;
                  setMessages(prev => prev.slice(0, -2));
                  void sendPrompt(retryText);
                }}
                style={{
                  fontSize: 12,
                  padding: '4px 12px',
                  borderRadius: 6,
                  border: '1px solid rgba(128,128,128,0.3)',
                  background: 'transparent',
                  cursor: 'pointer',
                  color: 'inherit',
                  opacity: 0.75,
                }}
              >
                ↺ Tentar novamente
              </button>
            </div>
          )}

          {isThinking && (
            <div className="bubble bubble-ai">
              <div className="bubble-sender">North IA</div>
              <div className="dot-loading" style={{ display: 'flex', gap: 5 }}>
                <span /><span /><span />
              </div>
            </div>
          )}

          {isHydratingHistory && (
            <div className="bubble bubble-ai">
              <div className="bubble-sender">North IA</div>
              Carregando histórico da última conversa...
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        <div className="ai-input-bar">
          <div className="ai-input-row">
            {ditado.suportado && (
              <BotaoMicrofone
                className="h-[52px] w-[52px]"
                ouvindo={ditado.ouvindo}
                transcrevendo={ditado.transcrevendo}
                desabilitado={isThinking || isHydratingHistory}
                aoAlternar={() => (ditado.ouvindo ? ditado.parar() : ditado.iniciar(prompt))}
              />
            )}
            <textarea
              className="ai-textarea"
              id="ai-prompt"
              placeholder={ditado.transcrevendo
                ? 'Transcrevendo áudio…'
                : ditado.ouvindo
                ? 'Gravando… clique no quadrado para encerrar'
                : prospeccao
                  ? 'Descreva o lead, a resposta do cliente ou peça sugestões de contato'
                  : 'Digite sua mensagem...'}
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={1}
              disabled={isHydratingHistory}
            />
            <button
              type="button"
              className="ai-send-btn"
              onClick={() => sendPrompt(prompt)}
              disabled={!prompt.trim() || isThinking || isHydratingHistory}
            >
              <SendHorizontal style={{ width: 16, height: 16 }} />
              Enviar
            </button>
          </div>
          <div className="ai-hint">
            <CornerDownLeft style={{ width: 11, height: 11 }} />
            Enter para enviar · Shift+Enter quebra linha
            {ditado.erro && <span style={{ color: '#e5484d', marginLeft: 8 }}>{ditado.erro}</span>}
            {ditado.transcrevendo && <span style={{ marginLeft: 8 }}>Ajustando o texto… Enter envia sem ajuste.</span>}
          </div>
        </div>
      </div>

      <AjudaNorthIA
        open={ajudaAberta}
        onClose={() => setAjudaAberta(false)}
        onUsarExemplo={(texto) => {
          setPrompt(texto);
          setAjudaAberta(false);
        }}
      />

      <ConversasNorthIA
        aberta={conversasAbertas}
        onAbertaChange={setConversasAbertas}
        conversaAtual={conversationId}
        onEscolher={(id) => {
          if (id !== conversationId) void abrirConversa(id);
        }}
        onNova={handleStartNewConversation}
        onApagada={(id) => {
          if (id === conversationId) handleStartNewConversation();
        }}
        versao={versaoConversas}
      />
    </>
  );
};
